import { ROUTE_PATHS, getDefaultHomePath } from '@config/routes';
import GalleryPageSkeleton from '@loaders/GalleryPageSkeleton';
import PageLoader from '@loaders/PageLoader';
import QuestsPageSkeleton from '@loaders/QuestsPageSkeleton';
import {
  ProtectedRoute,
  PublicRoute,
  OnboardingRoute,
  PermissionRoute,
  ResetPasswordRoute,
  PasswordChangeLock,
} from '@routes/RouteGuards';
import { PortalLayout, routes } from '@routes/portalRoutes';
import { Suspense, useMemo } from 'react';
import { useRoutes, Navigate } from 'react-router';

const SuspenseWrapper = ({ children }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>;

const suspensePage = (Page) =>
  Page ? (
    <SuspenseWrapper>
      <Page />
    </SuspenseWrapper>
  ) : null;

const skeletonPage = (Skeleton) => {
  function wrapPageWithSkeleton(Page) {
    return Page ? (
      <Suspense fallback={<Skeleton />}>
        <Page />
      </Suspense>
    ) : null;
  }

  wrapPageWithSkeleton.displayName = `skeletonPage(${Skeleton.displayName || Skeleton.name})`;

  return wrapPageWithSkeleton;
};

const publicPage = (Page) =>
  Page ? (
    <PublicRoute>
      <SuspenseWrapper>
        <Page />
      </SuspenseWrapper>
    </PublicRoute>
  ) : null;

const resetPasswordPage = (Page) =>
  Page ? (
    <ResetPasswordRoute>
      <SuspenseWrapper>
        <Page />
      </SuspenseWrapper>
    </ResetPasswordRoute>
  ) : null;

const onboardingPage = (Page) =>
  Page ? (
    <OnboardingRoute>
      <SuspenseWrapper>
        <Page />
      </SuspenseWrapper>
    </OnboardingRoute>
  ) : null;

const permissionPage = (permission) => {
  function wrapPageWithPermission(Page) {
    return Page ? (
      <PermissionRoute permission={permission}>
        <SuspenseWrapper>
          <Page />
        </SuspenseWrapper>
      </PermissionRoute>
    ) : null;
  }

  wrapPageWithPermission.displayName = `permissionPage(${permission})`;

  return wrapPageWithPermission;
};

const optionalRoute = (path, Page, wrap = suspensePage) =>
  Page ? { path, element: wrap(Page) } : null;

const AppRoutes = () => {
  const {
    LoginPage,
    ResetPasswordPage,
    DashboardPage,
    SetupGuidePage,
    GalleryPage,
    FeedPage,
    FeedFollowerDetailPage,
    TeamPage,
    TeamMemberCreatePage,
    TeamMemberAssignPage,
    TeamMemberManagePage,
    NudgesPage,
    CreateNudgePage,
    ManageNudgePage,
    ManageBusinessPage,
    ProfilePage,
    SettingsPage,
    UsersPage,
    AddAdminPage,
    SavvyNudgePage,
    QuestsPage,
    CreateQuestPage,
    ManageQuestPage,
    BusinessUsersPage,
    AddBusinessUserPage,
    MerchantUsersPage,
    MerchantUserDetailPage,
    MerchantUserEditProfilePage,
    ConsumersPage,
    ConsumerDetailPage,
    MerchantBusinessesPage,
    MerchantBusinessDetailPage,
    MerchantBusinessProfilePage,
    BrandBusinessesPage,
    VersionManagementPage,
    UpdateVersionBuildPage,
    BusinessQueriesPage,
    BusinessQueryDetailPage,
    ResourcesPage,
    ToolsPage,
    IconManagementPage,
    IconFormPage,
    CategoriesPage,
    CategoryFormPage,
    CategorySectionsPage,
    CategorySectionFormPage,
    CategorySubsectionsPage,
    CategorySubsectionFormPage,
    VerifyOtpPage,
    CreateProfilePage,
    SearchBusinessPage,
    BusinessDetailPage,
    RequestSubmittedPage,
    NotFoundPage,
    UnauthorizedPage,
  } = routes;

  const routeConfig = useMemo(
    () =>
      [
        optionalRoute(ROUTE_PATHS.LOGIN, LoginPage, publicPage),
        optionalRoute(ROUTE_PATHS.RESET_PASSWORD, ResetPasswordPage, resetPasswordPage),
        optionalRoute(ROUTE_PATHS.VERIFY_OTP, VerifyOtpPage, onboardingPage),
        optionalRoute(ROUTE_PATHS.CREATE_PROFILE, CreateProfilePage, onboardingPage),
        optionalRoute(ROUTE_PATHS.SEARCH_BUSINESS, SearchBusinessPage, onboardingPage),
        optionalRoute(ROUTE_PATHS.BUSINESS_DETAIL, BusinessDetailPage, onboardingPage),
        optionalRoute(ROUTE_PATHS.REQUEST_SUBMITTED, RequestSubmittedPage, onboardingPage),
        optionalRoute(ROUTE_PATHS.UNAUTHORIZED, UnauthorizedPage),
        {
          path: '/',
          element: (
            <ProtectedRoute>
              <SuspenseWrapper>
                <PortalLayout />
              </SuspenseWrapper>
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <Navigate to={getDefaultHomePath()} replace /> },
            optionalRoute('dashboard', DashboardPage),
            optionalRoute('launchpad', SetupGuidePage),
            { path: 'setup-guide', element: <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace /> },
            optionalRoute('gallery', GalleryPage, skeletonPage(GalleryPageSkeleton)),
            optionalRoute('feed', FeedPage),
            optionalRoute('feed/followers', FeedFollowerDetailPage),
            optionalRoute('team', TeamPage),
            optionalRoute('team/add', TeamMemberCreatePage),
            optionalRoute('team/add/permissions', TeamMemberAssignPage),
            optionalRoute('team/members', TeamMemberManagePage),
            optionalRoute('nudges', NudgesPage),
            optionalRoute('nudges/create', CreateNudgePage),
            optionalRoute('nudges/manage', ManageNudgePage),
            optionalRoute('manage-business', ManageBusinessPage),
            optionalRoute('profile', ProfilePage),
            optionalRoute('settings', SettingsPage, permissionPage('settings.view')),
            optionalRoute('users', UsersPage, permissionPage('users.view')),
            optionalRoute('users/add', AddAdminPage, permissionPage('users.view')),
            optionalRoute('users/manage', AddAdminPage, permissionPage('users.view')),
            optionalRoute('savvy-nudge', SavvyNudgePage),
            optionalRoute('quests', QuestsPage, skeletonPage(QuestsPageSkeleton)),
            optionalRoute('quests/create', CreateQuestPage),
            optionalRoute('quests/manage', ManageQuestPage),
            optionalRoute('business-users', BusinessUsersPage),
            optionalRoute('business-users/add', AddBusinessUserPage),
            optionalRoute('business-users/:userId', AddBusinessUserPage),
            optionalRoute('merchant-users', MerchantUsersPage),
            optionalRoute('merchant-users/manage', MerchantUserDetailPage),
            optionalRoute('merchant-users/manage/edit-profile', MerchantUserEditProfilePage),
            optionalRoute('consumers', ConsumersPage),
            optionalRoute('consumers/:userId', ConsumerDetailPage),
            optionalRoute('merchant-businesses', MerchantBusinessesPage),
            optionalRoute('merchant-businesses/manage', MerchantBusinessDetailPage),
            optionalRoute('merchant-businesses/manage/profile', MerchantBusinessProfilePage),
            optionalRoute('brand-businesses', BrandBusinessesPage),
            optionalRoute('version-management', VersionManagementPage),
            optionalRoute('version-management/add', UpdateVersionBuildPage),
            optionalRoute('business-queries', BusinessQueriesPage),
            optionalRoute('business-queries/manage', BusinessQueryDetailPage),
            optionalRoute('resources', ResourcesPage),
            optionalRoute('tools', ToolsPage),
            optionalRoute('icon-management', IconManagementPage),
            optionalRoute('icon-management/add', IconFormPage),
            optionalRoute('icon-management/edit', IconFormPage),
            optionalRoute('categories', CategoriesPage),
            optionalRoute('categories/add', CategoryFormPage),
            optionalRoute('categories/edit', CategoryFormPage),
            optionalRoute('categories/sections', CategorySectionsPage),
            optionalRoute('categories/sections/add', CategorySectionFormPage),
            optionalRoute('categories/sections/edit', CategorySectionFormPage),
            optionalRoute('categories/subsections', CategorySubsectionsPage),
            optionalRoute('categories/subsections/add', CategorySubsectionFormPage),
            optionalRoute('categories/subsections/edit', CategorySubsectionFormPage),
          ].filter(Boolean),
        },
        optionalRoute(ROUTE_PATHS.NOT_FOUND, NotFoundPage),
        { path: '*', element: <Navigate to={ROUTE_PATHS.NOT_FOUND} replace /> },
      ].filter(Boolean),
    [
      LoginPage,
      ResetPasswordPage,
      DashboardPage,
      SetupGuidePage,
      GalleryPage,
      FeedPage,
      FeedFollowerDetailPage,
      TeamPage,
      TeamMemberCreatePage,
      TeamMemberAssignPage,
      TeamMemberManagePage,
      NudgesPage,
      CreateNudgePage,
      ManageNudgePage,
      ManageBusinessPage,
      ProfilePage,
      SettingsPage,
      UsersPage,
      AddAdminPage,
      SavvyNudgePage,
      QuestsPage,
      CreateQuestPage,
      ManageQuestPage,
      BusinessUsersPage,
      AddBusinessUserPage,
      MerchantUsersPage,
      MerchantUserDetailPage,
      MerchantUserEditProfilePage,
      ConsumersPage,
      ConsumerDetailPage,
      MerchantBusinessesPage,
      MerchantBusinessDetailPage,
      MerchantBusinessProfilePage,
      BrandBusinessesPage,
      VersionManagementPage,
      UpdateVersionBuildPage,
      BusinessQueriesPage,
      BusinessQueryDetailPage,
      ResourcesPage,
      IconManagementPage,
      IconFormPage,
      VerifyOtpPage,
      CreateProfilePage,
      SearchBusinessPage,
      BusinessDetailPage,
      RequestSubmittedPage,
      NotFoundPage,
      UnauthorizedPage,
    ],
  );

  const element = useRoutes(routeConfig);

  return <PasswordChangeLock enabled={Boolean(ResetPasswordPage)}>{element}</PasswordChangeLock>;
};

export default AppRoutes;
